import React from 'react';
import SubPage from '../components/site/SubPage';
import { ClosingBrand, PageSection, PageTitleBox } from '../components/site/SubPageParts';

const IMG = '/images/site/predator-profile';

/*
 * Lure callouts drawn over the envelope photograph. Coordinates live in the
 * same 1050 x 300 space as the source image (10500 x 3000), so each box keeps
 * its position and each tail keeps pointing at the envelope at any width.
 *
 * `box` is [x, y, width, height]; `tail` is the wedge from the box edge to a
 * point on the envelope.
 */
const ENVELOPE_VIEWBOX = { w: 1050, h: 300 };

const LURES = [
  {
    label: ['Account Alert'],
    box: [183, 37, 174, 69],
    side: 'right',
    tailTop: 88,
    tailBottom: 106,
    apex: [428, 124],
  },
  {
    label: ['Payment Declined'],
    box: [752, 35, 213, 71],
    side: 'left',
    tailTop: 90,
    tailBottom: 106,
    apex: [616, 152],
  },
  {
    label: ['Sweepstakes'],
    box: [45, 150, 207, 71],
    side: 'right',
    tailTop: 150,
    tailBottom: 171,
    apex: [428, 152],
  },
  {
    label: ['Social Security', 'Update'],
    box: [792, 179, 211, 68],
    side: 'left',
    tailTop: 179,
    tailBottom: 199,
    apex: [616, 186],
  },
];

const LURE_FILL = '#2E5F7F';
const LURE_STROKE = '#14364F';

/**
 * Box and tail as one closed outline. Drawing them as separate shapes leaves a
 * seam where the tail meets the box, because both carry their own stroke.
 */
const calloutPath = ({ box, side, tailTop, tailBottom, apex }) => {
  const [x, y, width, height] = box;
  const right = x + width;
  const bottom = y + height;
  const [apexX, apexY] = apex;

  if (side === 'right') {
    return [
      `M ${x} ${y}`,
      `H ${right}`,
      `V ${tailTop}`,
      `L ${apexX} ${apexY}`,
      `L ${right} ${tailBottom}`,
      `V ${bottom}`,
      `H ${x}`,
      'Z',
    ].join(' ');
  }

  return [
    `M ${right} ${y}`,
    `H ${x}`,
    `V ${tailTop}`,
    `L ${apexX} ${apexY}`,
    `L ${x} ${tailBottom}`,
    `V ${bottom}`,
    `H ${right}`,
    'Z',
  ].join(' ');
};

const SCAMMER_ACTIONS = [
  'Trust-based scams.',
  'Social engineering tactics to deceive their targets.',
  'Phishing / smishing / spoofing schemes to connect with targets.',
];
const SCAMMER_MOTIVES = ['Money.', 'Psychology dominance.'];

const HACKER_ACTIONS = [
  'Unauthorized access to systems / networks.',
  'Vandalize websites.',
  'Corporate or political espionage.',
  'Build malicious software — ransomware.',
];
const HACKER_MOTIVES = [
  'Motivated by social issues or political activism.',
  'Money — ransomware or selling stolen data on the black market.',
];

const SCAM_TYPES = [
  {
    title: 'Trust Scams',
    body: 'Scammers create fake profiles and scripts by AI chat bot to make speech more believable. In 2025, victims lost over $19 million to Confidence / Romance scams with a likely AI-nexus.',
  },
  {
    title: 'Investment Scams',
    body: 'AI-generated videos with fake, professional-looking endorsements on social media or in video calls. In 2025, losses to Investment scams exceeded $8 billion.',
  },
  {
    title: 'Sextortion',
    body: 'Can start on any site, app or messaging platform. Cybercriminals may say they have a revealing picture or video. They threaten to publish the content or threaten violence, compelling the victim to produce more images. In 2025, over 75,000 submissions regarding sextortion.',
  },
];

/** Arrow chip that prefixes each capability bullet. */
const ArrowBullet = ({ children, framed = false }) => (
  <li className="flex items-start gap-4">
    <span
      aria-hidden="true"
      className={`mt-0.5 flex h-7 w-7 flex-shrink-0 items-center justify-center text-[15px] ${
        framed ? 'border border-black text-black' : 'bg-black text-white'
      }`}
    >
      &#8599;
    </span>
    <span className="text-[15px] leading-snug text-black md:text-[17px]">{children}</span>
  </li>
);

const ProfileHeading = ({ children }) => (
  <h2 className="text-[26px] font-bold text-black md:text-[36px]">/ {children}</h2>
);

const SubHeading = ({ children }) => (
  <h3 className="text-[20px] font-bold text-black md:text-[26px]">{children}</h3>
);

const PredatorProfile = () => (
  <SubPage title="Scammer Profile">
    <PageTitleBox>Understand your enemy&rsquo;s.</PageTitleBox>

    {/* ---------------- Phishing lures ---------------- */}
    <PageSection className="pt-4">
      <div className="relative">
        <img
          src={`${IMG}/phishing-envelope.jpeg`}
          alt="A red envelope dangling from a fish hook"
          className="block w-full"
        />
        <svg
          viewBox={`0 0 ${ENVELOPE_VIEWBOX.w} ${ENVELOPE_VIEWBOX.h}`}
          className="absolute inset-0 h-full w-full"
          role="list"
          aria-label="Common phishing lures"
        >
          {LURES.map((lure) => {
            const [x, y, width, height] = lure.box;
            const lineHeight = 26;
            const firstLineY =
              y + height / 2 + 8 - ((lure.label.length - 1) * lineHeight) / 2;
            return (
              <g key={lure.label.join(' ')} role="listitem">
                <path
                  d={calloutPath(lure)}
                  fill={LURE_FILL}
                  stroke={LURE_STROKE}
                  strokeWidth="1.5"
                  strokeLinejoin="miter"
                />
                <text
                  x={x + width / 2}
                  y={firstLineY}
                  textAnchor="middle"
                  fontSize="24"
                  fill="#FFFFFF"
                >
                  {lure.label.map((line, i) => (
                    <tspan key={line} x={x + width / 2} dy={i === 0 ? 0 : lineHeight}>
                      {line}
                    </tspan>
                  ))}
                </text>
              </g>
            );
          })}
        </svg>
      </div>

      <img
        src={`${IMG}/senior-couple.jpeg`}
        alt="An older couple reviewing paperwork at a laptop"
        className="mt-2 h-[240px] w-full object-cover object-top small:aspect-[336/125] small:h-auto"
      />
    </PageSection>

    {/* ---------------- Scammer ---------------- */}
    <PageSection className="pt-12">
      <ProfileHeading>Scammer.</ProfileHeading>
      <div className="mt-3 h-px w-full bg-black/25" />

      <div className="mt-6 grid grid-cols-1 gap-8 lg:grid-cols-2 lg:gap-14">
        {/* Caption above the photo, matching the Hacker block below. */}
        <div>
          <p className="text-[17px] text-black md:text-[22px]">
            High degree of technical capability.
          </p>
          <img
            src={`${IMG}/scammer.jpeg`}
            alt="A figure in a hat and suit hunched over a laptop in the dark"
            className="mt-4 h-[280px] w-full object-cover small:aspect-[23/15] small:h-auto"
          />
        </div>

        <div>
          <SubHeading>What Scammers do?</SubHeading>
          <ul className="mt-4 space-y-4">
            {SCAMMER_ACTIONS.map((item) => (
              <ArrowBullet key={item}>{item}</ArrowBullet>
            ))}
          </ul>

          <div className="mt-8">
            <SubHeading>What motivates Scammers?</SubHeading>
            <ul className="mt-4 space-y-4">
              {SCAMMER_MOTIVES.map((item) => (
                <ArrowBullet key={item}>{item}</ArrowBullet>
              ))}
            </ul>
          </div>
        </div>
      </div>

      <div className="mt-10 grid grid-cols-1 gap-8 md:grid-cols-3 md:gap-12">
        {SCAM_TYPES.map((type) => (
          <div key={type.title}>
            <h3 className="text-[17px] font-bold text-black md:text-[20px]">{type.title}</h3>
            <p className="mt-1 text-[15px] leading-snug text-black md:text-[17px]">{type.body}</p>
          </div>
        ))}
      </div>
    </PageSection>

    {/* ---------------- Hacker ---------------- */}
    <PageSection className="pt-14">
      <ProfileHeading>Hacker.</ProfileHeading>
      <div className="mt-3 h-px w-full bg-black/25" />

      <div className="mt-6 grid grid-cols-1 gap-8 lg:grid-cols-2 lg:gap-14">
        <div>
          <p className="text-[17px] text-black md:text-[22px]">
            High degree of technical capability.
          </p>
          <img
            src={`${IMG}/hacker.jpeg`}
            alt="A hooded figure dissolving into streams of code"
            className="mt-4 h-[280px] w-full object-cover small:aspect-[23/15] small:h-auto"
          />
        </div>

        <div>
          <SubHeading>What Hackers do?</SubHeading>
          <ul className="mt-4 space-y-4">
            {HACKER_ACTIONS.map((item) => (
              <ArrowBullet key={item} framed>
                {item}
              </ArrowBullet>
            ))}
          </ul>

          <div className="mt-8">
            <SubHeading>What motivates Hackers?</SubHeading>
            <ul className="mt-4 space-y-4">
              {HACKER_MOTIVES.map((item) => (
                <ArrowBullet key={item} framed>
                  {item}
                </ArrowBullet>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </PageSection>

    {/* ---------------- Closing banner ---------------- */}
    {/* 8-27: the wordmark no longer sits over the photo; it closes the page
        in the brand block below instead. */}
    <PageSection className="pt-12">
      <img
        src={`${IMG}/laptop-hands.jpeg`}
        alt="Hands typing on a laptop, shot from above"
        className="h-[260px] w-full object-cover object-right small:aspect-[168/55] small:h-auto"
      />
    </PageSection>

    <ClosingBrand tagline="There’s never been a better way to surf." />
  </SubPage>
);

export default PredatorProfile;
